import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Fade, Grow } from '@mui/material';
import { useAuth } from '../context/AuthContext';

const SplashScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, loading } = useAuth();
  const [showBook, setShowBook] = useState(false);

  useEffect(() => {
    console.log('Splash screen mounted');
    
    // Show book after 800ms
    const bookTimer = setTimeout(() => {
      setShowBook(true);
    }, 800);

    // Navigate after 3.5s or when auth resolves
    const navTimer = setTimeout(() => {
      if (!loading) {
        if (currentUser) {
          navigate('/home');
        } else {
          navigate('/login');
        }
      }
    }, 3500);

    return () => {
      clearTimeout(bookTimer);
      clearTimeout(navTimer);
    };
  }, [navigate, currentUser, loading]);

  // Navigate when auth resolves
  useEffect(() => {
    if (!loading && showBook) {
      const delayTimer = setTimeout(() => {
        if (currentUser) {
          navigate('/home');
        } else {
          navigate('/login');
        }
      }, 1500);

      return () => clearTimeout(delayTimer);
    }
    
    return () => {}; // Return cleanup function for all code paths
  }, [loading, currentUser, navigate, showBook]);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #8B4513 0%, #D2B48C 100%)',
        color: '#FFF8F0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background texture */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(255,248,240,0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,248,240,0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255,248,240,0.1) 0%, transparent 50%)
          `,
        }}
      />

      {/* Book Container */}
      <Grow in={showBook} timeout={2000}>
        <Box
          sx={{
            perspective: '1000px',
            transformStyle: 'preserve-3d',
            position: 'relative',
          }}
        >
          {/* Book */}
          <Box
            sx={{
              width: '280px',
              height: '350px',
              position: 'relative',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Book Cover - Stays Closed */}
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #654321 0%, #8B4513 100%)',
                borderRadius: '8px 12px 12px 8px',
                border: '3px solid #4A2C17',
                boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2,
              }}
            >
              {/* Cover Content */}
              <Fade in={showBook} timeout={3000}>
                <Box sx={{ textAlign: 'center', px: 3 }}>
                  {/* Logo */}
                  <Box
                    component="img"
                    src="/assets/images/splash_image.png"
                    alt="Cook with Otto"
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'contain',
                      mb: 2,
                      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
                    }}
                  />
                  
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      lineHeight: 1.2,
                    }}
                  >
                    Cook with Otto
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      fontStyle: 'italic',
                      opacity: 0.9,
                      mb: 3,
                    }}
                  >
                    Recipe Collection
                  </Typography>

                  {/* Decorative elements */}
                  <Box
                    sx={{
                      width: '60px',
                      height: '2px',
                      backgroundColor: '#FFF8F0',
                      margin: '0 auto',
                      opacity: 0.7,
                    }}
                  />
                </Box>
              </Fade>

              {/* Book spine decoration */}
              <Box
                sx={{
                  position: 'absolute',
                  left: -3,
                  top: 0,
                  bottom: 0,
                  width: '12px',
                  background: 'linear-gradient(to bottom, #4A2C17 0%, #2C1810 100%)',
                  borderRadius: '8px 0 0 8px',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Grow>
    </Box>
  );
};

export default SplashScreen;
